<template>
  <div class="border-solid border-0 border-t border-gray-border pt-4">
    <div class="flex items-center">
      <checkbox id="productRecommendationEnabled"
                :is-checked="config.enabled"
                :is-disabled="isDisabled"
                @update:isChecked="update('enabled', $event)"
      />
      <label for="productRecommendationEnabled"
             class="ml-2 text-gray-dark leading-tight"
             :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'"
      >
        This answer recommends products
      </label>
      <vs-tooltip position="top" class="ml-2 flex items-center">
        <template #content>
          <span class="text-xs">
            Adds the built-in recommendation instructions (the goal, the process, how to present products)
            to this answer, so they don't have to be written into the System prompt.
            Leave it off for FAQ answers such as Orders or Returns.
          </span>
        </template>
        <feather-icon icon="InfoIcon" svg-classes="h-4 w-4 text-gray"/>
      </vs-tooltip>
    </div>

    <div v-if="config.enabled" class="mt-4 flex flex-col gap-4">
      <div>
        <base-label id="relevantInformation"
                    :label="RELEVANT_INFORMATION.label"
                    :tooltip="RELEVANT_INFORMATION.tooltip"
        />
        <t-textarea id="relevantInformation"
                    name="relevantInformation"
                    :value="config.relevantInformation"
                    :placeholder="isDisabled ? '' : RELEVANT_INFORMATION.placeholder"
                    class="mt-1 border-gray focus:border-focus"
                    :variant="isDisabled ? 'textSectionDisabled' : 'textSection'"
                    :maxlength="RELEVANT_INFORMATION.lengthLimit"
                    rows="4"
                    :disabled="isDisabled"
                    @input="update('relevantInformation', $event)"
        />
      </div>

      <div>
        <base-label id="productLayout" :label="LAYOUT.label" :tooltip="LAYOUT.tooltip"/>
        <base-select :variant="isDisabled ? 'baseSelectDisabled' : 'baseSelect'"
                     :value="selectedLayoutText"
                     :options="layoutOptions"
                     class="mt-1 h-10"
                     :is-disabled="isDisabled"
                     @input="updateLayout"
        />
      </div>

      <vx-tooltip :text="QUICK_FILTERS.tooltip" position="top" class="flex items-center">
        <checkbox id="productQuickFilters"
                  :is-checked="config.quickFilters"
                  :is-disabled="isDisabled"
                  @update:isChecked="update('quickFilters', $event)"
        />
        <label for="productQuickFilters"
               class="ml-2 text-gray-dark leading-tight"
               :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'"
        >
          {{ QUICK_FILTERS.label }}
        </label>
      </vx-tooltip>

      <vx-tooltip :text="SHOW_CONTINUE.tooltip" position="top" class="flex items-center">
        <checkbox id="productShowContinue"
                  :is-checked="config.showContinue"
                  :is-disabled="isDisabled"
                  @update:isChecked="update('showContinue', $event)"
        />
        <label for="productShowContinue"
               class="ml-2 text-gray-dark leading-tight"
               :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'"
        >
          {{ SHOW_CONTINUE.label }}
        </label>
      </vx-tooltip>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/preview/Checkbox'
import conversationModule from '@/store/modules/conversation.module'
import {
  PRODUCT_RECOMMENDATION_DEFAULTS, PRODUCT_RECOMMENDATION_FIELDS, PRODUCT_LAYOUT_OPTIONS
} from '@/constants/product-recommendation.constants'

export default {
  name: 'ChatGPTEditorProductRecommendation',

  components: { Checkbox },

  props: {
    action: {
      type: Object,
      required: true
    },
    actionId: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ...PRODUCT_RECOMMENDATION_FIELDS,
      layoutOptions: PRODUCT_LAYOUT_OPTIONS.map(option => option.text)
    }
  },

  computed: {
    // Every key present whatever the saved action carries: actions authored before this section exists have
    // no `productRecommendation` at all, and the engine reads an absent key as these same defaults.
    config() {
      return { ...PRODUCT_RECOMMENDATION_DEFAULTS, ...(this.action.productRecommendation || {}) }
    },

    selectedLayoutText() {
      const option = PRODUCT_LAYOUT_OPTIONS.find(item => item.value === this.config.layout)
      return (option || PRODUCT_LAYOUT_OPTIONS[0]).text
    }
  },

  methods: {
    update(key, value) {
      if (this.isDisabled) return

      // review: switching the section off resets the rest rather than keeping values the form no longer shows.
      // The engine normalizes a disabled section to the defaults anyway, so keeping them here would only mean
      // re-enabling resurrects hidden settings the customer cannot see they still have.
      const productRecommendation = key === 'enabled' && !value ?
        { ...PRODUCT_RECOMMENDATION_DEFAULTS } :
        { ...this.config, [key]: value }

      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, productRecommendation })
    },

    updateLayout(text) {
      const option = PRODUCT_LAYOUT_OPTIONS.find(item => item.text === text)
      if (option) this.update('layout', option.value)
    }
  }
}
</script>
